import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const closeAll = useCallback(() => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const useTransparent = transparent && !isScrolled;
  const isLight = useTransparent;

  const textColor = isLight ? 'text-background-50' : 'text-foreground-900';
  const textMuted = isLight ? 'text-background-50/90' : 'text-foreground-700';
  const textHover = isLight ? 'hover:text-background-50 hover:bg-white/10' : 'hover:text-foreground-900 hover:bg-background-100';
  const borderColor = isLight ? 'border-background-50/40' : 'border-background-200/70';
  const outlineBtn = isLight
    ? 'text-background-50 border border-background-50/40 hover:bg-white/10'
    : 'text-foreground-700 border border-background-200/70 hover:bg-background-100 hover:text-foreground-900';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md'
      } ${(!isLight && isScrolled) || !transparent ? 'shadow-sm' : ''}`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap flex-shrink-0" onClick={closeAll}>
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center">
              <i className={`ri-grid-fill text-xl md:text-2xl ${isLight ? 'text-background-50' : 'text-primary-500'}`}></i>
            </div>
            <span className={`font-heading text-lg md:text-xl font-bold ${textColor}`}>
              OOPBuy
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1.5">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted} ${textHover}`}
            >
              Home
            </Link>
            <Link
              to="/guide/oopbuy-spreadsheet-complete-guide"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted} ${textHover}`}
            >
              Shopping Guide
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted} ${textHover}`}
              >
                Categories
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-lg shadow-lg border border-background-200/70 py-1 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className={`md:hidden px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${textMuted} ${textHover}`}
            >
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden sm:inline-flex px-3 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${outlineBtn}`}
            >
              OOPBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${textMuted} ${textHover}`}
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto" ref={mobileMenuRef}>
          <div className="px-4 py-4">
            <Link
              to="/guide/oopbuy-spreadsheet-complete-guide"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer mb-1"
              onClick={closeAll}
            >
              <div className="w-8 h-8 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-primary-500"></i>
              </div>
              <span className="text-sm font-medium">Shopping Guide</span>
            </Link>
            <div className="mt-4 mb-2 px-3">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wide">Product Categories</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={closeAll}
                >
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                  </div>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}